import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ArtworkPurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const provider = searchParams.get('provider');
  const paypalToken = searchParams.get('token');

  useEffect(() => {
    if (provider === 'paypal' && paypalToken) {
      capturePayPal();
      return;
    }

    if (!sessionId) {
      setStatus('failed');
      return;
    }

    pollStripe();
  }, []);

  const capturePayPal = async () => {
    try {
      const res = await axios.post(`${API}/artwork-purchase/paypal-capture`, null, {
        params: { paypal_order_id: paypalToken, order_id: orderId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data.status === 'success' ? 'success' : 'failed');
    } catch {
      setStatus('failed');
    }
  };

  const pollStripe = async () => {
    if (attempts >= 15) { setStatus('failed'); return; }
    try {
      const res = await axios.get(`${API}/artwork-purchase/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        setAttempts(a => a + 1);
        setTimeout(pollStripe, 2000);
      }
    } catch {
      setAttempts(a => a + 1);
      setTimeout(pollStripe, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-amber-50 pt-24 flex items-start justify-center" data-testid="artwork-purchase-success-page">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-amber-900 mb-2">{t('payment.processing', '支付确认中...')}</h2>
              <p className="text-amber-600">{t('payment.pleaseWait', '请稍候')}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-amber-900 mb-2">{t('payment.success', '购买成功!')}</h2>
              <p className="text-amber-600 mb-6">{t('artwork.purchaseSuccessMsg', '原作购买成功，艺术家将尽快寄出作品')}</p>
              <div className="space-y-3">
                <Button className="w-full" onClick={() => navigate('/orders')}>
                  {t('order.viewOrders', '查看我的订单')}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => navigate('/artworks')}>
                  {t('artwork.viewAllArtworks', '继续浏览')}
                </Button>
              </div>
            </>
          )}
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-amber-900 mb-2">{t('payment.failed', '支付失败')}</h2>
              <p className="text-amber-600 mb-6">{t('payment.tryAgain', '请重试或联系客服')}</p>
              <Button variant="outline" className="w-full" onClick={() => navigate(-1)}>
                {t('common.back', '返回')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ArtworkPurchaseSuccessPage;
